// src/screens/student/collaborativeboard/CollaborativeBoard.js
import React, { useState, useRef, useEffect } from 'react';
import '../../../assets/css/student/collaborativeboard/CollaborativeBoard.css';
import StudentLayout from '../../../layouts/student/studentlayout/StudentLayout';

const CollaborativeBoard = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const snapshotRef = useRef(null);
  const [selectedGroup, setSelectedGroup] = useState('react');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeUsers, setActiveUsers] = useState([
    { id: 1, name: 'John Doe', avatar: 'J', color: '#3e77d1', active: true },
    { id: 2, name: 'Sarah Smith', avatar: 'S', color: '#10b981', active: true },
    { id: 3, name: 'Mike Johnson', avatar: 'M', color: '#8b5cf6', active: false }
  ]);

  const groups = [
    { id: 'react', name: 'React Advanced Study' },
    { id: 'dsa', name: 'Data Structures & Algorithms' },
    { id: 'ml', name: 'Machine Learning Basics' }
  ];

  const colors = [
    '#000000', '#ef4444', '#f59e0b', '#10b981',
    '#3e77d1', '#8b5cf6', '#ec4899', '#ffffff'
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Set canvas size
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (tool === 'text') {
      const text = prompt("Enter text");
      if (text) {
        ctx.fillStyle = color;
        ctx.font = `${lineWidth * 5}px Arial`;
        ctx.fillText(text, x, y);
      }
      return;
    }

    setStartPos({ x, y });
    setIsDrawing(true);

    snapshotRef.current = canvas.toDataURL();

    if (tool === 'pen' || tool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Pen & Eraser
    if (tool === 'pen' || tool === 'eraser') {
      ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
      ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    // Rectangle Shape
    if (tool === 'shape') {
      const img = new Image();
      img.src = snapshotRef.current;

      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const width = x - startPos.x;
        const height = y - startPos.y;

        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.strokeRect(startPos.x, startPos.y, width, height);
      };
    }
  };

  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const undo = () => {
    if (!history.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const newHistory = [...history];
    const last = newHistory.pop();

    setRedoStack(prev => [...prev, last]);
    setHistory(newHistory);

    const img = new Image();
    img.src = newHistory[newHistory.length - 1];

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (img.src) ctx.drawImage(img, 0, 0);
    };
  };

  const redo = () => {
    if (!redoStack.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const newRedo = [...redoStack];
    const last = newRedo.pop();

    setRedoStack(newRedo);
    setHistory(prev => [...prev, last]);

    const img = new Image();
    img.src = last;

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
  };

  const stopDrawing = () => {
    setIsDrawing(false);

    const canvas = canvasRef.current;
    setHistory(prev => [...prev, canvas.toDataURL()]);
    setRedoStack([]);
  };

  const clearCanvas = () => {
    if (window.confirm('Clear the entire board? This cannot be undone.')) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  const saveBoard = () => {
    setShowSaveModal(true);
  };

  const downloadBoard = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `whiteboard-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
    setShowSaveModal(false);
  };

  const handleSaveToNotes = () => {
    console.log('Saving to notes...');
    alert('Board saved to notes successfully!');
    setShowSaveModal(false);
  };

  return (
    <StudentLayout>
      <div className="collaborative-board">
        {/* Header */}
        <div className="board-header">
          <div className="header-left">
            <div className="page-title-icon">✏️</div>
            <div>
              <h1 className="page-title">White Board</h1>
              <div className="board-info">
                <select
                  className="group-select"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
                <span className="separator">•</span>
                <span className="active-users-count">
                  👥 {activeUsers.filter(u => u.active).length} active
                </span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline" onClick={saveBoard}>
              <span>💾</span>
              Save
            </button>
            <button className="btn btn-outline" onClick={downloadBoard}>
              <span>📥</span>
              Download
            </button>
            <button className="btn btn-danger" onClick={clearCanvas}>
              <span>🗑️</span>
              Clear
            </button>
          </div>
        </div>

        <div className="board-content">
          {/* Toolbar */}
          <div className="toolbar">
            <div className="toolbar-section">
              <div className="section-title">Tools</div>
              <div className="tool-buttons">
                <button
                  className={`tool-btn ${tool === 'pen' ? 'active' : ''}`}
                  onClick={() => setTool('pen')}
                  title="Pen Tool"
                >
                  ✏️
                </button>
                <button
                  className={`tool-btn ${tool === 'eraser' ? 'active' : ''}`}
                  onClick={() => setTool('eraser')}
                  title="Eraser Tool"
                >
                  🧹
                </button>
                <button
                  className={`tool-btn ${tool === 'text' ? 'active' : ''}`}
                  onClick={() => setTool('text')}
                  title="Text Tool"
                >
                  📝
                </button>
                <button
                  className={`tool-btn ${tool === 'shape' ? 'active' : ''}`}
                  onClick={() => setTool('shape')}
                  title="Shape Tool"
                >
                  ⬜
                </button>
              </div>
            </div>

            <div className="toolbar-section">
              <div className="section-title">Colors</div>
              <div className="color-palette">
                {colors.map((c) => (
                  <button
                    key={c}
                    className={`color-btn ${color === c ? 'active' : ''}`}
                    style={{
                      backgroundColor: c,
                      border: c === '#ffffff' ? '2px solid #e5e7eb' : '2px solid transparent'
                    }}
                    onClick={() => setColor(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div className="toolbar-section">
              <div className="section-title">Brush Size</div>
              <input
                type="range"
                min="1"
                max="20"
                value={lineWidth}
                onChange={(e) => setLineWidth(Number(e.target.value))}
                className="brush-slider"
              />
              <div
                className="brush-preview"
                style={{
                  width: `${lineWidth * 3}px`,
                  height: `${lineWidth * 3}px`,
                  backgroundColor: color === '#ffffff' ? '#000000' : color
                }}
              />
            </div>

            <div className="toolbar-section">
              <div className="section-title">Active Users</div>
              <div className="users-list">
                {activeUsers.map((user) => (
                  <div key={user.id} className="user-item">
                    <div
                      className="user-avatar"
                      style={{ backgroundColor: user.color }}
                    >
                      {user.avatar}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{user.name}</div>
                      <div className={`user-status ${user.active ? 'online' : 'offline'}`}>
                        {user.active ? '🟢 Drawing' : '⚫ Idle'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="toolbar-section">
              <button className="btn btn-primary btn-full">
                <span>💬</span>
                Open Chat
              </button>
            </div>
          </div>

          {/* Canvas */}
          <div className="canvas-container">
            <div className="canvas-wrapper">
              <canvas
                ref={canvasRef}
                className="whiteboard-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              <div className="canvas-overlay">
                <div className="zoom-controls">
                  <button className="zoom-btn">➖</button>
                  <span className="zoom-level">100%</span>
                  <button className="zoom-btn">➕</button>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="canvas-actions">
              <button className="action-btn" title="Undo" onClick={undo}>↶</button>
              <button className="action-btn" title="Redo" onClick={redo}>↷</button>
            </div>
          </div>
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
            <div className="modal-content save-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Save Whiteboard</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowSaveModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="save-options">
                <div className="save-option" onClick={handleSaveToNotes}>
                  <div className="option-icon">📝</div>
                  <div className="option-content">
                    <h3>Save to Notes</h3>
                    <p>Add this board to your study notes</p>
                  </div>
                </div>

                <div className="save-option" onClick={downloadBoard}>
                  <div className="option-icon">📥</div>
                  <div className="option-content">
                    <h3>Download as Image</h3>
                    <p>Save as PNG file to your device</p>
                  </div>
                </div>

                <div className="save-option" onClick={() => {
                  alert('Share feature coming soon!');
                  setShowSaveModal(false);
                }}>
                  <div className="option-icon">📤</div>
                  <div className="option-content">
                    <h3>Share with Group</h3>
                    <p>Share this board with group members</p>
                  </div>
                </div>

                <div className="save-option" onClick={() => {
                  alert('Cloud save feature coming soon!');
                  setShowSaveModal(false);
                }}>
                  <div className="option-icon">☁️</div>
                  <div className="option-content">
                    <h3>Save to Cloud</h3>
                    <p>Store in your cloud storage</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tutorial Hint */}
        <div className="tutorial-hint">
          <div className="hint-content">
            💡 <strong>Tip:</strong> Use Ctrl+Z to undo and Ctrl+Y to redo
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default CollaborativeBoard;